import React, {useMemo, useState} from 'react';
import AppButton from '../../../../../../components/common/AppButton';
import {SVG} from '../../../../../../constants/assetLists';
import ChatBox, {
  ChatType,
} from '../../../AnnouncementsAndTasks/components/ChatBox';
import ChatInput from '../../../AnnouncementsAndTasks/components/ChatInput';
import {FILES_INPUT} from '../../AddAnnouncement/data';
import RenderFiles from '../../AddAnnouncement/components/RenderFiles';
import {useTranslation} from 'react-i18next';
import AppFileInputButton from '../../../../../../components/common/AppFileInputButton';

type GenericObject = Record<string, any>;

type Props = {
  index: number;
  item: any;
  state: any;
  dispatch: any;
  setChecklist: React.Dispatch<React.SetStateAction<GenericObject>>;
  toggleChecklistItem: (index: number) => void;
  enableComments?: boolean;
};

function ApprovalChecklist({
  index,
  item,
  state,
  dispatch,
  setChecklist,
  toggleChecklistItem,
  enableComments,
}: Props) {
  const {i18n} = useTranslation();

  const [selectedChat, setSelectedChat] = useState<any>(null);

  const {addedText, selectedIndex, selectedSection, enableCommentField} = state;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    if (!files) return;

    const existingFiles = item.files || [];
    const existingAddedFiles = item.addedFiles || [];

    const newFiles = Array.from(files);
    const fileNames = new Set(
      existingFiles.map((f: any) => f.name + f.size) || [],
    );
    const addedFilesNames = new Set(
      existingAddedFiles.map((f: any) => f.name + f.size) || [],
    );

    const uniqueNewFiles = newFiles.filter(
      f => !fileNames.has(f.name + f.size),
    );
    const uniqueAddedFiles = newFiles.filter(
      f => !addedFilesNames.has(f.name + f.size),
    );

    setChecklist(prev => ({
      ...prev,
      list: prev.list.map((item: any, idx: number) =>
        idx === index
          ? {
              ...item,
              files: [
                ...existingFiles,
                // ...uniqueNewFiles
              ],
              addedFiles: [...existingAddedFiles, ...uniqueAddedFiles],
            }
          : item,
      ),
    }));

    e.target.value = '';
  };

  const handleFileRemove = (fileName: string) => {
    const allFiles = item.files;
    const userFiles = item.addedFiles;

    setChecklist(prev => ({
      ...prev,
      list: prev.list.map((item: any, idx: number) =>
        idx === index
          ? {
              ...item,
              files: allFiles.filter((ele: any) => ele.name !== fileName),
              addedFiles: userFiles.filter((ele: any) => ele.name !== fileName),
            }
          : item,
      ),
    }));
  };

  const handleCommentAddDelete = (type: 'add' | 'delete') => {
    setChecklist(prev => ({
      ...prev,
      list: prev.list.map((item: GenericObject, idx: number) => {
        if (idx !== index) return item;

        // work with a fresh copy of this one item’s comments
        const comments = item.comments ?? [];
        const userComments = item.addedComments ?? [];
        const uniqueId = Math.floor(Math.random() * 1000000);
        let newComments = comments,
          addedComments = userComments;
        let userName = localStorage.getItem('userName');

        if (type === 'add') {
          const userCommentExits = userComments.find(
            (c: GenericObject) => c.id === selectedChat?.id,
          );

          if (userCommentExits) {
            // update existing comment
            addedComments = userComments.map((c: GenericObject) =>
              c.id === selectedChat.id ? {...c, comment: addedText} : c,
            );
          } else {
            // append new comment
            addedComments = [
              ...userComments,
              {
                id: uniqueId,
                commentBy: {
                  firstName: userName?.split(' ')[0],
                  lastName: userName?.split(' ')[1],
                },
                comment: addedText,
                updatedAt: new Date(),
              },
            ];
          }
        } else {
          // delete mode
          newComments = comments.filter(
            (c: GenericObject) => c.id !== selectedChat?.id,
          );

          addedComments = userComments.filter(
            (c: GenericObject) => c.id !== selectedChat?.id,
          );
        }

        return {
          ...item,
          comments: newComments,
          addedComments: addedComments,
        };
      }),
    }));
    setSelectedChat(null);
    dispatch({
      type: 'ENABLE_DISABLE_COMMENT_FIELD',
    });
  };

  const handleSectionSelect = () => {
    dispatch({
      type: 'OPEN_CLOSE_SECTION',
      payload: {
        idx:
          index == selectedIndex && selectedSection !== 'expand' ? null : index,
        option:
          index == selectedIndex && selectedSection == 'section'
            ? null
            : 'section',
      },
    });
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    dispatch({
      type: 'ADD_COMMENT',
      payload: e.target.value,
    });
  };

  const handleBoxClick = (chat: ChatType) => {
    setSelectedChat(chat);
    dispatch({
      type: 'EDIT_COMMENT',
      payload: chat?.comment,
    });
  };

  const handleBtnClick = () => {
    dispatch({
      type: 'ENABLE_DISABLE_COMMENT_FIELD',
    });
  };

  const isHebrew = useMemo(() => i18n.language === 'heb', [i18n.language]);
  const isSelected = useMemo(
    () => selectedIndex === index && selectedSection == 'section',
    [index, selectedIndex, selectedSection],
  );
  const filesLen = useMemo(
    () => (item?.files?.length ?? 0) + (item?.addedFiles?.length ?? 0),
    [item],
  );
  const commentsLen = useMemo(
    () => (item?.comments?.length ?? 0) + (item?.addedComments?.length ?? 0),
    [item],
  );
  const hasMetaData = useMemo(
    () => filesLen > 0 || commentsLen > 0,
    [filesLen, commentsLen],
  );

  return (
    <div
      className={`px-2 py-1 border-b border-b-lightDarkGray group hover:bg-softAquaLight 
    ${isSelected && 'bg-softAquaLight shadow-md'}`}>
      <div className="flex flex-col gap-2 ">
        <div className="flex items-start gap-1 relative">
          {hasMetaData && (
            <div
              className={`absolute top-[2px] flex items-center gap-2  ${
                isHebrew ? 'left-0' : 'right-0'
              }`}>
              {filesLen > 0 && (
                <div className="flex items-center gap-1">
                  <img
                    src={SVG.imageSmallIcon}
                    alt="icon"
                    className="w-[18px] h-[18px] object-contain"
                  />
                  <p className="m-0 text-[20px] font-medium text-black">
                    {filesLen}
                  </p>
                </div>
              )}

              {commentsLen > 0 && (
                <div className="flex items-center gap-1">
                  <img
                    src={SVG.chatSmallIcon}
                    alt="icon"
                    className="w-[18px] h-[18px] object-contain"
                  />
                  <p className="m-0 text-[20px] font-medium text-black">
                    {commentsLen}
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-1 ">
            <p className="m-0 text-[20px] font-light text-gray w-[40px]">
              {(index + 1).toString().padStart(2, '0')}
            </p>
          </div>

          <div
            className={`
            flex-1 flex items-start gap-2 min-w-0
            ${hasMetaData ? 'pl-20' : ''}
            `}>
            {/* ← Critical: min-w-0 */}
            <button
              className="w-[20px] h-[20px] mt-[5px] flex-shrink-0"
              onClick={() => toggleChecklistItem(index)}>
              <img
                src={
                  item.isApproved
                    ? SVG.completedCheckboxIcon
                    : SVG.regularCheckboxIcon
                }
                alt="icon"
                className="w-full h-full object-contain"
              />
            </button>
            <button
              type="button"
              className={`
              flex-1 block text-[20px] font-medium text-right
              break-all         
              word-break: break-all;
              overflow-hidden
              leading-snug
              ${item.checked ? 'text-black' : 'text-gray-500'}
            `}
              onClick={handleSectionSelect}>
              {item.text}
            </button>
          </div>
        </div>

        {isSelected && (
          <>
            <div className="flex flex-col gap-3">
              {item.addedComments?.map((chat: ChatType) => {
                return (
                  <ChatBox
                    key={chat._id}
                    chat={chat}
                    boxBg={'#fff'}
                    circleBg={'#1AC3F2'}
                    onBoxClick={() => handleBoxClick(chat)}
                  />
                );
              })}

              <div className="flex flex-col gap-2">
                {item.addedFiles.map((file: any, fileIndex: number) => {
                  return (
                    <RenderFiles
                      key={fileIndex}
                      file={file}
                      index={fileIndex}
                      handleFileRemove={() => {
                        handleFileRemove(file.name);
                      }}
                    />
                  );
                })}
              </div>

              {enableCommentField && (
                <ChatInput
                  chat={selectedChat}
                  value={addedText}
                  disableTick={addedText?.length === 0}
                  disableDelete={!selectedChat}
                  onChange={handleCommentChange}
                  onTickClick={() => {
                    if (addedText) {
                      handleCommentAddDelete('add');
                    }
                  }}
                  onDeleteClick={() => {
                    handleCommentAddDelete('delete');
                  }}
                />
              )}

              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  {FILES_INPUT.map(item => (
                    <AppFileInputButton
                      id={item.id}
                      icon={item.icon}
                      disabled={addedText?.length > 0}
                      accept={item.accept}
                      multiple
                      onChange={handleFileChange}
                    />
                  ))}
                </div>

                {enableComments && (
                  <AppButton
                    variant="custom"
                    iconStart={
                      <img
                        src={SVG.addIcon}
                        className="w-[12px] h-[12px] object-contain"
                        alt="add"
                      />
                    }
                    disabled={addedText?.length > 0}
                    className={`rounded-lg text-gray px-3 py-1 bg-white border-[1px] border-gray hover:!bg-mintGreen transition-colors duration-200
                      ${addedText?.length > 0 && "opacity-40 hover:!bg-white"}`}
                    onClick={handleBtnClick}
                  >
                    הוספת טקסט
                  </AppButton>
                )}
              </div>
            </div>

            {(item.comments?.length > 0 || item.files?.length > 0) && (
              <div>
                <div className="w-full h-[1px] bg-lightDarkGray my-2" />
                <div className="px-[20px] py-[16px] bg-gray95 flex flex-col gap-3">
                  <div className="flex flex-col gap-2">
                    {item.comments?.map((chat: ChatType) => (
                      <ChatBox
                        key={chat._id}
                        chat={chat}
                        boxBg={'#fff'}
                        circleBg={'#1AC3F2'}
                      />
                    ))}
                  </div>

                  <div className="flex flex-col gap-2">
                    {item.files.map((file: any, fileIndex: number) => (
                      <RenderFiles
                        key={fileIndex}
                        file={file}
                        index={fileIndex}
                        showRemoveIcon={false}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ApprovalChecklist;
